import type { JobState, ReviewFinding } from '@api/devices/contracts';
import { Ban, CircleCheck, CircleHelp, CircleX, Clock, Hourglass, KeyRound, Loader2, Send, ShieldAlert, TriangleAlert, Info } from 'lucide-react';
import { cn } from '../ui/cn';

export const jobStateMeta: Record<JobState, { label: string; icon: typeof Clock; className: string; hint: string }> = {
  awaiting_approval: { label: 'Needs approval', icon: KeyRound, className: 'text-attention bg-attention-soft', hint: 'Nothing runs until someone with approval rights confirms it with a security key.' },
  rejected: { label: 'Rejected', icon: Ban, className: 'text-ink-3 bg-sunken', hint: 'An approver declined this request.' },
  queued: { label: 'Queued', icon: Clock, className: 'text-ink-2 bg-sunken', hint: 'Waiting for the device to collect it.' },
  delivered: { label: 'Sent to device', icon: Send, className: 'text-ink-2 bg-sunken', hint: 'The device has collected the job.' },
  running: { label: 'Running', icon: Loader2, className: 'text-series bg-series/10', hint: 'The device reported that it started.' },
  succeeded: { label: 'Succeeded', icon: CircleCheck, className: 'text-healthy bg-healthy-soft', hint: 'The device reported success.' },
  failed: { label: 'Failed', icon: CircleX, className: 'text-offline bg-offline-soft', hint: 'The device reported a failure.' },
  canceled: { label: 'Canceled', icon: Ban, className: 'text-ink-3 bg-sunken', hint: 'Canceled before it finished.' },
  expired: { label: 'Expired', icon: Hourglass, className: 'text-ink-3 bg-sunken', hint: 'The device did not collect it in time. Nothing ran.' },
  unknown_outcome: { label: 'Outcome unknown', icon: CircleHelp, className: 'text-attention bg-attention-soft',
    hint: 'The device took the job but did not report back in time. Check the device before running it again.' },
};

export function JobStateBadge({ state, className }: { state: JobState; className?: string }) {
  const m = jobStateMeta[state], Icon = m.icon;
  return (
    <span title={m.hint} className={cn('inline-flex h-6 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 text-[12.5px] font-medium', m.className, className)}>
      <Icon className={cn('size-3.5', state === 'running' && 'animate-spin')} />{m.label}
    </span>
  );
}

const findingStyle = { high: { icon: ShieldAlert, className: 'text-offline' }, medium: { icon: TriangleAlert, className: 'text-attention' }, info: { icon: Info, className: 'text-ink-3' } };
export function ReviewList({ findings, compact = false }: { findings: ReviewFinding[]; compact?: boolean }) {
  if (!findings.length) return null;
  return (
    <ul className="space-y-1.5">
      {findings.map((f, i) => {
        const { icon: Icon, className } = findingStyle[f.severity];
        return (
          <li key={i} className="flex gap-2 text-[13px]">
            <Icon className={cn('mt-0.5 size-4 shrink-0', className)} />
            <span className="min-w-0">
              <span className="font-medium text-ink">{f.title}</span>{f.line ? <span className="text-ink-3"> · line {f.line}</span> : null}
              {!compact && <span className="block text-ink-2">{f.detail}</span>}
              {!compact && f.excerpt && <code className="mt-0.5 block truncate rounded bg-sunken px-1.5 py-0.5 font-mono text-[12px] text-ink-2">{f.excerpt}</code>}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/** Keep text boundaries and quoting visible before the person signs the job. */
export function JobParameters({ parameters }: { parameters: Record<string, unknown> }) {
  return <div className="space-y-1 font-mono">{Object.entries(parameters).map(([name, value]) =>
    <div key={name} className="whitespace-pre-wrap break-all">{name} = {JSON.stringify(value)}</div>)}</div>;
}
